<template>
    <div>
        <div class="row frame-model-row">
            <label for="frameModelChoice" class="termslabel lp frame-model-label">
                <strong>Which frame model will you be using for your Evaluation Lenses? *</strong> &nbsp;
            </label>
            <div class="frame-model-select-wrapper">
                <select
                    id="frameModelChoice"
                    v-model="sampleobject.frameModel"
                    @change="fetchUpcOptions(sampleobject.frameModel)"
                    v-validate="'required'"
                    class="orderinput-select"
                    name="Frame Model Choice"
                >
                    <option v-for="choice in frameModelChoices" :value="choice.value" :key="choice.value">
                        {{ choice.label }}
                    </option>
                </select>
                <p class="error" v-if="errors.has('Frame Model Choice')">{{ errors.first('Frame Model Choice') }}</p>
            </div>
        </div>

        <div class="row" v-if="sampleobject.frameModel === 2 || sampleobject.raybanMeta === 'Yes'">
            <p class="info" :style="{ color: 'red' }">
                Since you will be using Ray-Ban Meta frame, this will be a LENS ONLY order. Do not ship your Ray-Ban Meta frame to lab.
            </p>
        </div>
        <div class="row" v-if="sampleobject.frameModel === 4 || sampleobject.raybanMeta === 'Yes'">
            <p class="info" :style="{ color: 'red' }">
                Since you will be using Oakley Meta frame, this will be a LENS ONLY order. Do not ship your Oakley Meta frame to lab.
            </p>
        </div>

        <div class="row mb-2" v-if="sampleobject.frameModel === 3" style="margin-bottom: 18px;">
            <p class="info" :style="{ color: 'red' }">
                Since you will be using Nuance audio frame, this will be a LENS ONLY order. Do not ship your Nuance audio frame to lab.
            </p>
            <a
                href="/reference/Mounting-Guide-Nuance.pdf"
                download
                class="download-button text-blue"
            >
                Download
            </a>
            the Lens Mounting Guide for Nuance Audio frame to keep for future reference and help you mount your lenses.
            <br/>
        </div>
        <div class="row" v-if="sampleobject.frameModel !== 2 && sampleobject.frameModel !== 3 && sampleobject.frameModel !== 4">
            <label for="framesready" class="termslabel lp frame-question">
                <strong>Do you have your frames ready to ship to the lab for processing the Product? *</strong>
                <div class="button-group">
                    <button 
                        type="button" 
                        :class="{'selected': sampleobject.framesReady === true}" 
                        @click="setFramesReady(true)">
                        Yes
                    </button>
                    <button 
                        type="button" 
                        :class="{'selected': sampleobject.framesReady === false}" 
                        @click="setFramesReady(false)">
                        No
                    </button>
                </div>
            </label>
            <div class="row" v-show="false">
                <label for="framesready" class="termslabel lp">
                    <input v-model="sampleobject.framesReady" id="framesready" name="Frames ready" v-validate="isRequired" type="checkbox" class="termscheck"> I have my frames ready to ship to the lab for processing the Product.
                </label>
            </div>  
            <div :class="{'error': sampleobject.framesReady === false || errors.has('Frames ready')}">
                <p class="error" v-if="sampleobject.framesReady === false">
                    It is important not to submit the order until you have your frames ready to ship. This will reduce delays in matching the job with the correct frame.
                </p>
                <p class="error" v-if="errors.has('Frames ready') && sampleobject.framesReady === null">
                    Frames Ready is a required field.
                </p>
            </div>
        </div>

        <div class="row" v-if="sampleobject.frameModel === 2 || sampleobject.frameModel === 3 || sampleobject.frameModel === 4">
            <label for="acceptedTerms" class="termslabel lp">
                <input v-model="sampleobject.acceptedTerms" id="acceptedTerms" name="accepted Terms" v-validate="isRequired" type="checkbox" class="termscheck"> I understand that I will not have the abilitiy to have my lenses remade
                in the event that the process is done incorrectly. The Rx, Measurement, and Frame Information I have entered is correct and accurate.
            </label>
        </div>

        <div :key="sampleobject.frameModel" class="row">
            <div v-if="sampleobject.frameModel === 1" class="columns small-12">
                <!-- Frame Manufacturer, Model, and Color fields maximized -->
                <div class="columns small-12 medium-4 lp">
                    <labeled-input validation="required|max:20" label="Frame Manufacturer *" name="Frame Manufacturer" v-model='sampleobject.manufacturer'></labeled-input>
                </div>
                <div class="columns small-12 medium-4 mp">
                    <labeled-input validation="required|max:50" label="Frame Model *" name="Frame Model" v-model='sampleobject.model'></labeled-input>
                </div>
                <div class="columns small-12 medium-4 rp">
                    <labeled-input validation="required|max:50" label="Frame Color *" name="Frame Color" v-model='sampleobject.color'></labeled-input>
                </div>
            </div>
            <div v-else-if="sampleobject.frameModel === 2 || sampleobject.frameModel === 3 || sampleobject.frameModel === 4">
                <!-- First line: FRAME UPC (aligned to manufacturer width) -->
                <div class="row">
                  <div class="small-12 medium-3 columns lp">
                    <div :class="{ 'error': errors.has('Frame UPC Dropdown') }" style="position: relative;">
                      <div class="upc-label-row" style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
                        <label class="label-inline" for="frameUpcDropdown">Frame UPC *</label>
                        <!-- inline help trigger; tooltip-content is its child -->
                        <a class="help-link text-blue" tabindex="0" aria-label="Frame UPC help">
                          Need help?
                          <span class="tooltip-content">
                            <span v-if="sampleobject.frameModel === 2">
                              Here’s where you can find the FRAME UPC information from your Ray-ban Meta frame box
                              <img src="/images/rayban-upc-tooltip.png" alt="Frame UPC Example">
                            </span>
                            <span v-if="sampleobject.frameModel === 3">
                              Here’s where you can find the FRAME UPC information from your Nuance audio frame box
                              <img src="/images/nuance-upc-tooltip.png" alt="Frame UPC Example">
                            </span>
                              <span v-if="sampleobject.frameModel === 4">
                              Here’s where you can find the FRAME UPC information from your Oakley Meta frame box
                              <img src="/images/oakley-meta-upc-tooltip.jpg" alt="Frame UPC Example">
                            </span>
                          </span>
                        </a>
                      </div>
                      <select
                        id="frameUpcDropdown"
                        v-model="sampleobject.upc"
                        @change="onUpcChange"
                        v-validate="'required'"
                        class="orderinput-select"
                        name="Frame UPC Dropdown"
                      >
                        <option v-for="item in upcOptions" :value="item.UPC" :key="item.UPC">
                          {{ item.UPC }}
                        </option>
                      </select>
                      <p class="error" v-if="errors.has('Frame UPC Dropdown')">{{ errors.first('Frame UPC Dropdown') }}</p>
                    </div>
                  </div>
                </div>

                <!-- Second line: Manufacturer, Model, Color, Frame Type -->
                <div class="row" style="margin-top:12px;">
                  <div class="small-12 medium-3 columns lp">
                    <labeled-input :disabled="upcRequired" label="Frame Manufacturer" name="Frame Manufacturer" v-model='sampleobject.manufacturer'></labeled-input>
                  </div>
                  <div class="small-12 medium-4 columns mp">
                    <labeled-input :disabled="upcRequired" label="Frame Model" name="Frame Model" v-model='sampleobject.model'></labeled-input>
                  </div>
                  <div class="small-12 medium-3 columns rp">
                    <labeled-input :disabled="upcRequired" label="Frame Color" name="Frame Color" v-model='sampleobject.color'></labeled-input>
                  </div>
                  <div class="small-12 medium-2 columns rp">
                    <div :class="{'error': errors.has('Frame Type') }">
                      <label for="frame_Type">Frame Type </label>
                      <select v-model="sampleobject.type" name="Frame Type" id="frame_Type" class="orderinput-select" :disabled="upcRequired">
                        <option value="ZYL">Zyl Edge</option>
                        <option value="METAL">Metal Edge</option>
                        <option value="DRILL">Drilled Rimless</option>
                        <option value="DRILL2">Drill 2 Holes</option>
                        <option value="DRILL4">Drill 4 Holes</option>
                        <option value="DRILL6">Drill 6 Holes</option>
                        <option value="DRILL8">Drill 8 Holes</option>
                        <option value="GROOVE">Grooved Rimless</option>
                        <option value="FACET">Facet Edge</option>
                        <option value="MHALF">1/2 Eye Metal</option>
                        <option value="HALF">1/2 Eye Zyl</option>
                        <option value="FULL">Full Metal Groove</option>
                      </select>
                      <p class="error" v-if="errors.has('Frame Type')">{{ errors.first('Frame Type') }}</p>
                    </div>
                  </div>
                </div>

                <!-- Third line: Temple Length, A Box, B Box, DBL -->
                <div class="row" style="margin-top:12px;">
                  <div class="small-12 medium-3 columns lp">
                    <labeled-input autocomplete="off" :disabled="upcRequired" label="Temple Length" name="Temple Length" v-model='sampleobject.templeLength'></labeled-input>
                  </div>
                  <div class="small-12 medium-3 columns mp">
                    <labeled-input autocomplete="off" :disabled="upcRequired"  label="A Box" name="A Box" v-model='sampleobject.aBox'></labeled-input>
                  </div>
                  <div class="small-12 medium-3 columns mp">
                    <labeled-input autocomplete="off" :disabled="upcRequired" label="B Box" name="B Box" v-model='sampleobject.bBox'></labeled-input>
                  </div>
                  <div class="small-12 medium-3 columns rp">
                    <labeled-input autocomplete="off" :disabled="upcRequired" label="DBL" name="DBL" v-model='sampleobject.dbl'></labeled-input>
                  </div>
                </div>
             </div>
        </div>
        
        <div class="row" v-if="!upcRequired">
            <div class="medium-3 columns lp">
                <div :class="{'error': errors.has('Frame Type') }">
                    <label for="frame_Type">Frame Type *</label>
                    <select v-validate="'required'" v-model="sampleobject.type" name="Frame Type" id="frame_Type" class="orderinput-select">
                        <option value="ZYL">Zyl Edge</option>
                        <option value="METAL">Metal Edge</option>
                        <option value="DRILL">Drilled Rimless</option>
                        <option value="DRILL2">Drill 2 Holes</option>
                        <option value="DRILL4">Drill 4 Holes</option>
                        <option value="DRILL6">Drill 6 Holes</option>
                        <option value="DRILL8">Drill 8 Holes</option>
                        <option value="GROOVE">Grooved Rimless</option>
                        <option value="FACET">Facet Edge</option>
                        <option value="MHALF">1/2 Eye Metal</option>
                        <option value="HALF">1/2 Eye Zyl</option>
                        <option value="FULL">Full Metal Groove</option>
                    </select>
                    <p class="error" v-if="errors.has('Frame Type')">{{ errors.first('Frame Type') }}</p>
                </div>
            </div>
            <div class="medium-3 columns mp">
                <labeled-input autocomplete="off" validation="required|numeric" label="Temple Length *" name="Temple Length" v-model='sampleobject.templeLength' class="aligned-temple"></labeled-input>
            </div>
            <div class="medium-2 columns mp">
                <labeled-input autocomplete="off" validation="required|decimal:0|between:10,80" label="A Box*" name="A Box" v-model='sampleobject.aBox'></labeled-input>
            </div>
            <div class="medium-2 columns mp">
                <labeled-input autocomplete="off" validation="required|decimal:0|between:10,80" label="B Box*" name="B Box" v-model='sampleobject.bBox'></labeled-input>
            </div>
            <div class="medium-2 columns rp">
                <labeled-input autocomplete="off" validation="required|decimal:0|between:0,35" label="DBL*" name="DBL" v-model='sampleobject.dbl'></labeled-input>
            </div>
        </div>

        <div class="row">
        </div>

        <div class="row" v-if="program.allowUncut">
            <div class="columns small-12 lp">
                <label for="allowUncut" class="small-text" style="display: flex; align-items: center;">
                    <input v-model="sampleobject.uncut" id="allowUncut" name="Allow uncut" type="checkbox" style="margin-right: 10px;">
                    <p class="small-text" style="margin: 0;">
                        I would like to order uncut lenses. I understand that I will have to edge and mount these on my own, and confirm that my location has the ability and know-how to do so. I understand that I will not have the ability to have my lenses remade in the event that the process is done incorrectly. The Rx, Measurement, and Frame Information I have entered is correct and accurate.
                    </p>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import LabeledInput from "@/components/shared/LabeledInput";

export default {
  props: ["sampleobject", "program"],
  inject: ["$validator"],
  components: { LabeledInput },
  data() {
    return {
      frameModelChoices: [
        { label: "Standard Frame", code: "other", value: 1 },
        { label: "Ray-ban Meta", code: "rayban_meta", value: 2 },
        { label: "Nuance Audio", code: "nuance_audio", value: 3 },
        { label: "Oakley Meta", code: "oakley_meta", value: 4 },
      ],
      upcOptions: [],
      showTooltip: false,
    };
  },
  computed: {
    isRequired() {
      return this.sampleobject.uncut ? "" : "required";
    },
    upcRequired() {
      return [2,3,4].includes(this.sampleobject.frameModel);
    }
  },
  created() {
    this.fetchUpcOptions(this.sampleobject.frameModel);
    this.$set(this.sampleobject, 'frameModel', this.sampleobject.frameModel || 1);
    if (this.sampleobject.framesReady == null) {
        this.$set(this.sampleobject, 'framesReady', null); // Ensure framesReady is initialized only if not already set
    }
  },
  methods: {
    clearAutoPopulatedFields() {
        const keys = ['upc', 'manufacturer', 'model', 'color', 'type', 'aBox', 'bBox', 'dbl', 'templeLength'];
        keys.forEach(k => this.$set(this.sampleobject, k, null));
        const errorNames = [
        'Frame Type', 'Temple Length', 'A Box', 'B Box', 'DBL',
        'Frame UPC Dropdown', 'Frame Manufacturer', 'Frame Model', 'Frame Color'
        ];
        errorNames.forEach(name => {
         try { this.$validator.errors.remove(name); } catch (e) {}
        });
    },

    fetchUpcOptions(frameModel) {
        this.clearAutoPopulatedFields();
        this.sampleobject.LensOnly = frameModel === 2 || frameModel === 3 || frameModel === 4;
         const apiEndpoints = {
             3: "/coupon/frame/Nuance",
             2: "/coupon/frame/Ray%20Ban%20Meta",
             4: "/coupon/frame/Oakley%20Meta"
         };
         if (frameModel === 1) {
            // ensure cleared
            this.clearAutoPopulatedFields();
            this.upcOptions = [];
             return;
         }
         this.$set(this.sampleobject, 'frameModel', frameModel);
         this.$nextTick(() => {
             if (apiEndpoints[frameModel]) {
                window.axios
                    .get(apiEndpoints[frameModel])
                    .then(response => {
                    this.upcOptions = response.data || [];
                    })
                    .catch(err => {
                    console.error('Failed to fetch UPC options:', err);
                    });
            } else {
                this.upcOptions = [];
                this.sampleobject.upc = null;
            }
        });
    },
    handleRaybanMetaChange() {
         this.sampleobject.LensOnly = this.sampleobject.frameModel === 2;
         this.sampleobject.acceptedTerms = false;
     },
    onUpcChange() {
      if (!this.sampleobject.upc) return;
      const item = this.upcOptions.find(i => i.UPC === this.sampleobject.upc);
      if (!item) return;
      this.sampleobject.manufacturer = item.Manufacturer;
      this.sampleobject.model = item.Model;
      this.sampleobject.color = item.Color;
      this.sampleobject.type = item.Type;
      this.sampleobject.aBox = item.ABox;
      this.sampleobject.bBox = item.BBox;
      this.sampleobject.dbl = item.Dbl 
      this.sampleobject.templeLength = item.TempleLength;
    },
    setFramesReady(value) {
        this.$set(this.sampleobject, 'framesReady', value);
    },
  }
};
</script>

<style>
.frame-model-row {
    display: flex;
    align-items: center;
}

.frame-model-label {
    flex: 0 0 auto;
    text-align: left;
    margin: 0;
}

.frame-model-select-wrapper {
    flex: 1; 
}
.frame-question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.frame-question span {
    flex: 1;
    margin-right: 10px;
}

.button-group {
    display: flex;
    gap: 10px;
    margin-right: 40px;
}

.button-group button {
    padding: 10px 20px;
    border: 1px solid #ccc;
    background-color: #f9f9f9;
    cursor: pointer;
    transition: background-color 0.3s;
    font-size: 16px;
    font-weight: bold;
}
.button-group button.selected {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
}
.button-group button:hover {
    background-color: #e0e0e0;
}
.label-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.label-wrapper label {
  margin: 0;
}

.label-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.info-icon {
  position: relative;
  cursor: pointer;
  text-decoration: underline;
  color: #007bff;
  margin-left: 6px;
  font-size: 13px;
}

.tooltip-content {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  padding: 12px;
  z-index: 3000;
  width: 340px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  white-space: normal;
}

.help-link {
  position: relative;
  text-decoration: underline;
  cursor: pointer;
  color: #007bff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.help-link:hover .tooltip-content,
.help-link:focus .tooltip-content,
.help-link:active .tooltip-content {
  display: block;
}

.tooltip-content img {
  display: block;
  margin-top: 10px;
  max-width: 320px;
  width: 100%;
  height: auto;
  border-radius: 4px;
}
.mb-2 {
    margin-bottom: 18px;
}
</style>

